import { Cartesian3 } from "cesium";

/**
 * @param arr Array of coordinates
 * @param n Number of elements per nested array [[n...],[n...]]
 */
function* arrayChunker(arr: any[], n: number) {
	for (let i = 0; i < arr.length; i += n) {
		yield arr.slice(i, i + n);
	}
}

// Convert HEX (#FFFFFF) to RGBA ()
// From  https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
function hexToRgb(hex: string, alpha?: number): [number, number, number, number] {
	const hexWithoutHash = hex.replace('#', '');
	const bigint = parseInt(hexWithoutHash, 16);
	const r = (bigint >> 16) & 255;
	const g = (bigint >> 8) & 255;
	const b = bigint & 255;

	if(alpha === undefined) {
		return [r, g, b, 255];
	} else {
		return [r, g, b, alpha * 255];
	}
}

// Convert RGB to Hex
// From https://stackoverflow.com/a/39077686
const rgbToHex = (r: number, g: number, b: number, alpha: number): string => {
	return (
		'#' +
		[r, g, b]
			.map((x) => {
				const hex = x.toString(16);
				return hex.length === 1 ? '0' + hex : hex;
			})
			.join('')
	);
};

/**
 * Iterates over an object structure and replaces all values with an empty string
 * @param obj - Object to change
 * @returns
 */
function replaceAllObjectValues(obj: any, val: any) {
	for (const property in obj) {
		if (Object.prototype.hasOwnProperty.call(obj, property)) {
			if (typeof obj[property] == 'object') {
				replaceAllObjectValues(obj[property], val);
			} else {
				obj[property] = val;
			}
		}
	}
	return obj;
}

/**
 * Returns a decimal-truncated number, no rounding (https://stackoverflow.com/questions/4187146/truncate-number-to-two-decimal-places-without-rounding)
 * @param num The number to truncate
 * @param fixed The number of decimal places
 * @returns The truncated number
 */
function truncateNumber(num: number, fixed: number): number {
	const re = new RegExp('^-?\\d+(?:.\\d{0,' + (fixed || -1) + '})?');
	return parseFloat(num?.toString().match(re)![0]);
}

function createEnum<T extends { [P in keyof T]: P }>(o: T) {
	return o;
}

/**
 * Some form values should be validated as numeric, but due to how browser inputs work, they might be strings.
 * This function takes an input and either returns it directly if it is already a number, or parses the input to a float value.
 * @param input
 */
const getFloatValue = ({ input }: { input: string | number }) => {
	if (typeof input === 'number') {
		return input;
	} else {
		return parseFloat(input);
	}
};

/**
 * A function that takes two strings and returns a Levehnstein-like comparison of likeness 
 * 
 * @param s1 String 1
 * @param s2 String 2
 */
const stringSimilarity = (s1: string, s2: string) => {
	var longer = s1;
	var shorter = s2;
	if (s1.length < s2.length) {
		longer = s2;
		shorter = s1;
	}
	var longerLength = longer.length;
		if (longerLength == 0) {
		return 1.0;
	}
		return (longerLength - editDistance(longer, shorter)) / parseFloat(longerLength.toString());
}

const editDistance = (s1: string, s2: string) => {
	s1 = s1.toLowerCase();
	s2 = s2.toLowerCase();

	var costs = new Array();
	for (var i = 0; i <= s1.length; i++) {
	var lastValue = i;
	for (var j = 0; j <= s2.length; j++) {
		if (i == 0)
		costs[j] = j;
		else {
		if (j > 0) {
			var newValue = costs[j - 1];
			if (s1.charAt(i - 1) != s2.charAt(j - 1))
			newValue = Math.min(Math.min(newValue, lastValue),
				costs[j]) + 1;
			costs[j - 1] = lastValue;
			lastValue = newValue;
		}
		}
	}
	if (i > 0)
		costs[s2.length] = lastValue;
	}
	return costs[s2.length];
}

const stringer = (input: any) => {
	return input ?? '';
};

// Display textual representation of boolean values.
function booler(input: any) {
	if (input == true || input == 'true') {
		return 'Yes';
	}
	return 'No';
}

const degreesToRatio = (degrees: number | null | undefined) => {
    if (!degrees) return 0;
    if (degrees === 0) return 0; // vertical
    if (degrees === 90) return Infinity; // horizontal (optional handling)

    const radians = (90 - degrees) * (Math.PI / 180);
    const x = 1 / Math.tan(radians);
    return Number(x.toFixed(1));
};

const ratioToDegrees = (x: number) => {
    if (x === 0) return 90;
    const deg = 90 - (Math.atan2(1, x) * 180) / Math.PI;
    return Number(deg.toFixed(2));
};

/**
 * Solves an issue where Svelte Forms Lib's `error` store is not properly typed
 * From http://enehana.nohea.com/general/svelte-complex-forms-part-3-components-using-get-setcontext-for-less-passing-props/
 * @param scope
 * @param str
 * @returns
 */
function getFormErrorMessage(scope: any, path: string) {
	let obj = scope,
		arr;

	try {
		arr = path
			.split(/[[\].]/) // split by [,],.
			.filter((el) => el) // filter out empty one
			.map((el) => el.replace(/^['"]+|['"]+$/g, '')); // remove string quotation
		arr.forEach((el) => (obj = obj[el]));
	} catch (e) {
		obj = undefined;
	}

	return obj;
}

/**
 * Format bytes as human-readable text.
 * 
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use 
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 * 
 * @return Formatted string.
 */
function humanFileSize(bytes: number, si = false, dp = 1) {
	const thresh = si ? 1000 : 1024;
  
	if (Math.abs(bytes) < thresh) {
	  return bytes + ' B';
	}
  
	const units = si 
	  ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] 
	  : ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
	let u = -1;
	const r = 10**dp;
  
	do {
	  bytes /= thresh;
	  ++u;
	} while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);
  
  
	return bytes.toFixed(dp) + ' ' + units[u];
  }

function findLowestDifference(arr: number[]) {
    // If 1 element or fewer, return 0
    if (arr.length < 2) return 0;

    arr.sort((a, b) => a - b);

    // Use Infinity to ensure the first difference found will be smaller
    let minDifference = Infinity;

    // Comparing adjacent elements
    for (let i = 0; i < arr.length - 1; i++) {
      const currentDifference = arr[i + 1] - arr[i];
  
      if (currentDifference < minDifference) {
        minDifference = currentDifference;
      }
      // If the difference is 0, it's the absolute minimum possible, so we can stop searching.
      if (minDifference === 0) {
        return 0;
      }
    }

    return minDifference;
  }

export {
	arrayChunker,
	hexToRgb,
	rgbToHex,
	truncateNumber,
	replaceAllObjectValues,
	createEnum,
	stringer,
	booler,
	getFloatValue,
	getFormErrorMessage,
	stringSimilarity,
	humanFileSize,
    findLowestDifference,
    degreesToRatio,
    ratioToDegrees
};
